/**
 * 马甲站站点配置表 + 身份解析 + 品牌资源 / token 注入
 *
 * 唯一事实来源：站点域名 → 域名简称 / 主体 appid / logo·lockup / CSS 变量 token。
 * 正式 appid 由服务端分配前，使用 VEST_ENTITY_APPID 占位；09 票只改这一处。
 * 加新站 = 配置表加一行 + 品牌图，零 scss 扫描。
 */

import qs from 'qs'
import { getEnv } from '@/utils/utils'

/** 主体 appid —— 服务端已分配（一主体一个，8 站按主体复用） */
export const VEST_ENTITY_APPID = {
  b25: 'cm1017-b25',
  sweet: 'cm1017-sweet'
} as const

export type VestEntity = keyof typeof VEST_ENTITY_APPID
export type SiteSlug = 'drama' | 'short' | 'binge' | 'watch'

/** 站点级品牌 CSS 变量（命名对齐原型 --rs-brand-*） */
export type SiteTokens = {
  /** 主品牌色 */
  '--rs-brand-normal': string
  /** 发送按钮禁用态 */
  '--rs-brand-send-disabled': string
  /**
   * 品牌强调色（history rate 等），缺省回落 --rs-brand-normal
   */
  '--rs-brand-accent': string
  /**
   * CTA / 提交按钮背景（纯色 / 渐变 / url(...) 均可）
   * 缺省回落 --rs-brand-normal
   */
  '--rs-cta-bg': string
  /**
   * brand bar logo 高度；缺省 24px。
   * 个别站（如 b25-short）原型略矮时在配置表覆写。
   */
  '--rs-logo-height': string
}

export interface SiteConfig {
  /** 站点域名，如 drama.b25reel.com（不含 feedback. 前缀） */
  domain: string
  /** 域名简称：drama / short / binge / watch */
  slug: SiteSlug
  /** 所属主体 */
  entity: VestEntity
  /** 主体 appid（占位或正式） */
  appid: string
  /**
   * 站点级唯一 class 标识（body 用），与原型文件命名对齐：
   * b25-drama / sweet-short …
   */
  siteKey: string
  /** brand bar logo（webpack url） */
  logo: string
  /** brand lockup 图（webpack url，可选；原型 b25-drama 无独立 lockup） */
  lockup?: string
  /**
   * 文字 brand name（仅 b25-drama 使用 mark + 文案形态；
   * 其余站 logo 自身含字标）
   */
  brandName?: string
  /** 展示名（img alt / 无障碍） */
  displayName: string
  /** 站点级 CSS 变量 token */
  tokens: SiteTokens
}

const SLUGS: SiteSlug[] = ['drama', 'short', 'binge', 'watch']

const ENTITY_ROOT_DOMAIN: Record<VestEntity, string> = {
  b25: 'b25reel.com',
  sweet: 'sweetmaxbest.com'
}

// —— 品牌资源（静态 require，保证 webpack 能打包进 allfb 产物）——

const brandLogoMark = require('../assets/vest/brands/logo-mark.svg')
const brandB25Short = require('../assets/vest/brands/B25Short.svg')
const brandB25Binge = require('../assets/vest/brands/B25Binge.svg')
const brandB25Watch = require('../assets/vest/brands/B25Watch.svg')
const brandSweetDrama = require('../assets/vest/brands/SweetDrama.svg')
const brandSweetShort = require('../assets/vest/brands/SweetShort.svg')
const brandSweetBinge = require('../assets/vest/brands/SweetBinge.svg')
const brandSweetWatch = require('../assets/vest/brands/SweetWatch.svg')

const lockupB25Short = require('../assets/vest/brands/b25-short-lockup.png')
const lockupB25Binge = require('../assets/vest/brands/b25-binge-lockup.png')
const lockupB25Watch = require('../assets/vest/brands/b25-watch-lockup.png')
const lockupSweetDrama = require('../assets/vest/brands/sweet-drama-lockup.png')
const lockupSweetShort = require('../assets/vest/brands/sweet-short-lockup.png')
const lockupSweetBinge = require('../assets/vest/brands/sweet-binge-lockup.png')
const lockupSweetWatch = require('../assets/vest/brands/sweet-watch-lockup.png')

const ctaB25Binge = require('../assets/vest/brands/B25Binge-light.png')
const ctaSweetShort = require('../assets/vest/brands/SweetShort-Button.png')
const ctaSweetWatch = require('../assets/vest/brands/SweetWatch-btn.png')

function ctaImageBg(url: string): string {
  return `url(${url}) center / 100% 100% no-repeat`
}

/**
 * 站点品牌表：按 siteKey 索引。
 * token 值对齐设计原型 HTML 内联 style。
 */
const SITE_BRAND_BY_KEY: Record<
  string,
  Pick<SiteConfig, 'logo' | 'lockup' | 'brandName' | 'displayName' | 'tokens'>
> = {
  'b25-drama': {
    logo: brandLogoMark,
    brandName: 'B25 DRAMA',
    displayName: 'B25Drama',
    tokens: {
      '--rs-brand-normal': '#f75722',
      '--rs-brand-send-disabled': 'rgba(247, 87, 34, 0.3)',
      '--rs-brand-accent': '#f75722',
      '--rs-cta-bg': '#f75722',
      '--rs-logo-height': '24px'
    }
  },
  'b25-short': {
    logo: brandB25Short,
    lockup: lockupB25Short,
    displayName: 'B25Short',
    tokens: {
      '--rs-brand-normal': '#6c47ff',
      '--rs-brand-send-disabled': 'rgba(108, 71, 255, 0.3)',
      '--rs-brand-accent': '#7c74ff',
      '--rs-cta-bg': '#6c47ff',
      // 原型 logo 略矮于默认 24px
      '--rs-logo-height': '22px'
    }
  },
  'b25-binge': {
    logo: brandB25Binge,
    lockup: lockupB25Binge,
    displayName: 'B25Binge',
    tokens: {
      '--rs-brand-normal': '#fa0e9b',
      '--rs-brand-send-disabled': 'rgba(232, 63, 17, 0.3)',
      '--rs-brand-accent': '#e83f11',
      '--rs-cta-bg': ctaImageBg(ctaB25Binge),
      '--rs-logo-height': '24px'
    }
  },
  'b25-watch': {
    logo: brandB25Watch,
    lockup: lockupB25Watch,
    displayName: 'B25Watch',
    tokens: {
      '--rs-brand-normal': '#3e1fb2',
      '--rs-brand-send-disabled': 'rgba(62, 31, 178, 0.3)',
      '--rs-brand-accent': '#8375ff',
      '--rs-cta-bg': 'linear-gradient(90deg, #969eff 0%, #3e1fb2 100%)',
      '--rs-logo-height': '24px'
    }
  },
  'sweet-drama': {
    logo: brandSweetDrama,
    lockup: lockupSweetDrama,
    displayName: 'SweetDrama',
    tokens: {
      '--rs-brand-normal': '#ff4876',
      '--rs-brand-send-disabled': 'rgba(255, 72, 118, 0.3)',
      '--rs-brand-accent': '#ff4c79',
      '--rs-cta-bg': '#ff4876',
      '--rs-logo-height': '24px'
    }
  },
  'sweet-short': {
    logo: brandSweetShort,
    lockup: lockupSweetShort,
    displayName: 'SweetShort',
    tokens: {
      '--rs-brand-normal': '#ff8e1c',
      '--rs-brand-send-disabled': 'rgba(255, 142, 28, 0.3)',
      '--rs-brand-accent': '#f75722',
      '--rs-cta-bg': ctaImageBg(ctaSweetShort),
      '--rs-logo-height': '24px'
    }
  },
  'sweet-binge': {
    logo: brandSweetBinge,
    lockup: lockupSweetBinge,
    displayName: 'SweetBinge',
    tokens: {
      '--rs-brand-normal': '#fc419c',
      '--rs-brand-send-disabled': 'rgba(252, 65, 156, 0.3)',
      '--rs-brand-accent': '#ff56de',
      '--rs-cta-bg':
        'linear-gradient(90deg, #fc6d5f 0%, #fc419c 33%, #ff50fb 100%)',
      '--rs-logo-height': '24px'
    }
  },
  'sweet-watch': {
    logo: brandSweetWatch,
    lockup: lockupSweetWatch,
    displayName: 'SweetWatch',
    tokens: {
      '--rs-brand-normal': '#ef2020',
      '--rs-brand-send-disabled': 'rgba(225, 46, 61, 0.3)',
      '--rs-brand-accent': '#e12e3d',
      '--rs-cta-bg': ctaImageBg(ctaSweetWatch),
      '--rs-logo-height': '24px'
    }
  }
}

/** 未匹配站点 / 异常域名时的兜底品牌（取 b25-drama，保证不白屏） */
export const DEFAULT_VEST_SITE_KEY = 'b25-drama'
export const DEFAULT_VEST_TOKENS: SiteTokens =
  SITE_BRAND_BY_KEY[DEFAULT_VEST_SITE_KEY].tokens

/** 注入到 documentElement 的品牌 token 键列表（清理时用） */
const BRAND_TOKEN_KEYS: (keyof SiteTokens)[] = [
  '--rs-brand-normal',
  '--rs-brand-send-disabled',
  '--rs-brand-accent',
  '--rs-cta-bg',
  '--rs-logo-height'
]

/**
 * rate 图标站点色变体（外链 <img> 吃不到 CSS 变量，按 siteKey 接线）。
 * 缺变体的站回落 DEFAULT rate.svg（b25-drama 橙）。
 */
const VEST_RATE_ICON_BY_SITE_KEY: Record<string, string> = {
  'b25-short': require('../assets/vest/icons/rate-b25-short.svg'),
  'b25-watch': require('../assets/vest/icons/rate-b25-watch.svg'),
  'sweet-drama': require('../assets/vest/icons/rate-sweet-drama.svg'),
  'sweet-binge': require('../assets/vest/icons/rate-sweet-binge.svg'),
  'sweet-watch': require('../assets/vest/icons/rate-sweet-watch.svg')
}

const VEST_RATE_ICON_DEFAULT: string = require('../assets/vest/icons/rate.svg')

function buildSiteConfig(entity: VestEntity, slug: SiteSlug): SiteConfig {
  const root = ENTITY_ROOT_DOMAIN[entity]
  const siteKey = `${entity}-${slug}`
  const brand = SITE_BRAND_BY_KEY[siteKey]
  if (!brand) {
    // 理论上 8 站全覆盖；兜底防止配置漏行导致运行时炸
    const fallback = SITE_BRAND_BY_KEY[DEFAULT_VEST_SITE_KEY]
    return {
      domain: `${slug}.${root}`,
      slug,
      entity,
      appid: VEST_ENTITY_APPID[entity],
      siteKey,
      ...fallback,
      displayName: siteKey
    }
  }
  return {
    domain: `${slug}.${root}`,
    slug,
    entity,
    appid: VEST_ENTITY_APPID[entity],
    siteKey,
    logo: brand.logo,
    lockup: brand.lockup,
    brandName: brand.brandName,
    displayName: brand.displayName,
    tokens: brand.tokens
  }
}

/** 8 站配置表：按站点域名索引 */
export const SITE_CONFIG_BY_DOMAIN: Record<string, SiteConfig> = (
  Object.keys(ENTITY_ROOT_DOMAIN) as VestEntity[]
).reduce((acc, entity) => {
  SLUGS.forEach((slug) => {
    const site = buildSiteConfig(entity, slug)
    acc[site.domain] = site
  })
  return acc
}, {} as Record<string, SiteConfig>)

/** 按 siteKey 索引（调试 / Layout 消费） */
export const SITE_CONFIG_BY_KEY: Record<string, SiteConfig> = Object.values(
  SITE_CONFIG_BY_DOMAIN
).reduce((acc, site) => {
  acc[site.siteKey] = site
  return acc
}, {} as Record<string, SiteConfig>)

export const VEST_APPID_LIST: string[] = Object.values(VEST_ENTITY_APPID)

/** sessionStorage 键：与 appid 同生命周期的站点标识 */
export const VEST_STORAGE_KEYS = {
  siteSlug: 'siteSlug',
  siteDomain: 'siteDomain',
  siteKey: 'siteKey',
  /** dev/test 调试：粘住的出站请求 appid 覆盖值（见 resolveDebugAppid） */
  debugAppid: 'debugAppid'
} as const

/** URL 参数名：本地/测试用站点覆盖（值为站点域名，如 drama.b25reel.com） */
export const VEST_SITE_URL_PARAM = 'site'

/**
 * URL 参数名：dev/test 调试用，覆盖马甲站「出站请求 appid」（借其它 appid 的
 * FAQ/表单/工单数据）。session 身份 / vest 皮肤不变，仅改发给后端的 appid。
 */
export const VEST_DEBUG_APPID_URL_PARAM = 'debug-appid'

/** debug-appid 复位值：URL 传 off 或空值 → 清除粘住的调试 appid */
const DEBUG_APPID_RESET_VALUES = ['off', '']

/**
 * 解析 dev/test 调试用的出站请求 appid 覆盖值（scope：仅覆盖请求身份，不动皮肤）。
 * - 仅 dev / test 生效，prod / gray 恒返回 null
 * - URL 带 debug-appid：off / 空 → 清 session 返回 null；否则写 session 粘住并返回
 * - URL 未带：回落 session 粘住值（hash 路由跳转 / redirect 冲掉参数后仍生效）
 * - 返回 null = 不覆盖，出站 appid 用站点原本主体 appid
 * 不做白名单校验（调试口子，填错仅表现为无数据，非 prod 无害）。
 */
export function resolveDebugAppid(
  hashParams?: Record<string, string>
): string | null {
  const env = getEnv()
  if (env !== 'dev' && env !== 'test') return null

  const params = hashParams || parseHashParams()
  const hasParam = Object.prototype.hasOwnProperty.call(
    params,
    VEST_DEBUG_APPID_URL_PARAM
  )

  try {
    if (hasParam) {
      const raw = (params[VEST_DEBUG_APPID_URL_PARAM] || '').trim()
      if (DEBUG_APPID_RESET_VALUES.includes(raw.toLowerCase())) {
        sessionStorage.removeItem(VEST_STORAGE_KEYS.debugAppid)
        return null
      }
      sessionStorage.setItem(VEST_STORAGE_KEYS.debugAppid, raw)
      return raw
    }
    const sticky = sessionStorage.getItem(VEST_STORAGE_KEYS.debugAppid)
    return sticky && sticky.trim() ? sticky : null
  } catch {
    return null
  }
}

export function isVestAppid(appid?: string | null): boolean {
  if (!appid) return false
  return VEST_APPID_LIST.includes(appid)
}

/**
 * 剥 test- / gray- / feedback. / feedback- 前缀，归一化为站点域名：
 *   feedback-drama.b25reel.com      → drama.b25reel.com（马甲站正式，连字符方案）
 *   test-feedback-drama.b25reel.com → drama.b25reel.com（马甲站测试）
 *   gray-feedback-drama.b25reel.com → drama.b25reel.com（来源端灰度入口，
 *                                     打到正式环境，流量靠 channel GRAY 区分）
 *   feedback.drama.b25reel.com      → drama.b25reel.com（旧点号方案，legacy）
 * 运维新方案用 [test-|gray-]feedback-<slug>.<base>（连字符融合）；旧点号形式
 * feedback.<域名> 保留作向后兼容。
 * 无前缀的 hostname 原样返回（兼容本地/测试直接挂站点域名的场景）。
 */
export function stripFeedbackPrefix(hostname: string): string {
  if (!hostname) return ''
  let lower = hostname.toLowerCase()
  if (lower.startsWith('test-')) {
    lower = lower.slice('test-'.length)
  } else if (lower.startsWith('gray-')) {
    lower = lower.slice('gray-'.length)
  }
  if (lower.startsWith('feedback.')) {
    lower = lower.slice('feedback.'.length)
  } else if (lower.startsWith('feedback-')) {
    lower = lower.slice('feedback-'.length)
  }
  return lower
}

function parseHashParams(): Record<string, string> {
  try {
    const raw = qs.parse(window.location?.hash?.split?.('?')?.[1] || '')
    return Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [
        decodeURIComponent(k),
        v == null ? '' : String(v)
      ])
    )
  } catch {
    return {}
  }
}

/**
 * 解析当前应使用的马甲站配置。
 * 优先级：URL `site` 参数覆盖 > hostname 解析。
 * 故意不回落 sessionStorage：避免本地测完马甲站后，同域既有 appid
 * 链接被缓存站点身份误覆盖。站点标识仍写入 sessionStorage 供下游读取。
 * 未命中任何马甲站时返回 null（既有 app 路径完全不受影响）。
 */
export function resolveVestSite(
  hashParams?: Record<string, string>
): SiteConfig | null {
  const params = hashParams || parseHashParams()

  // 1) URL 参数覆盖（本地 / 测试环境）
  const siteParam = (params[VEST_SITE_URL_PARAM] || '').toLowerCase().trim()
  if (siteParam) {
    const fromParam =
      SITE_CONFIG_BY_DOMAIN[siteParam] ||
      SITE_CONFIG_BY_DOMAIN[stripFeedbackPrefix(siteParam)]
    if (fromParam) return fromParam
  }

  // 2) hostname 解析（生产 feedback.<站点域名>）
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const siteDomain = stripFeedbackPrefix(window.location.hostname)
    const fromHost = SITE_CONFIG_BY_DOMAIN[siteDomain]
    if (fromHost) return fromHost
  }

  return null
}

/** 将站点身份写入 sessionStorage（appid + 站点标识） */
export function applyVestSiteToSession(site: SiteConfig): void {
  try {
    sessionStorage.setItem('appid', site.appid)
    sessionStorage.setItem(VEST_STORAGE_KEYS.siteSlug, site.slug)
    sessionStorage.setItem(VEST_STORAGE_KEYS.siteDomain, site.domain)
    sessionStorage.setItem(VEST_STORAGE_KEYS.siteKey, site.siteKey)

    const raw = sessionStorage.getItem('headerFromApp')
    if (raw) {
      const header = JSON.parse(raw)
      header.appid = site.appid
      sessionStorage.setItem('headerFromApp', JSON.stringify(header))
    }
  } catch {
    // sessionStorage 不可用时忽略
  }
}

/**
 * 将站点品牌 CSS 变量注入到 documentElement。
 * - site 命中：写入该站 tokens
 * - site 为 null 但 body 已是 vest：优先 session 恢复的站点 token，
 *   再回落 DEFAULT（避免 redirect 剥掉 ?site= 后被 b25-drama 覆盖）
 * - 非马甲站：清掉品牌 token，避免污染既有 app
 */
export function applyVestSiteTokens(site: SiteConfig | null): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  const shouldApply =
    !!site ||
    (typeof document.body !== 'undefined' &&
      document.body.classList.contains('vest'))

  if (!shouldApply && !site) {
    // 既有 app：确保不残留上次调试留下的品牌变量
    BRAND_TOKEN_KEYS.forEach((key) => {
      root.style.removeProperty(key)
    })
    return
  }

  // null 分支：session 站点优先于 DEFAULT，保住 redirect 后的品牌色
  const tokens: SiteTokens =
    site?.tokens || getCurrentVestSite()?.tokens || DEFAULT_VEST_TOKENS
  BRAND_TOKEN_KEYS.forEach((key) => {
    root.style.setProperty(key, tokens[key])
  })
}

/**
 * 在 App 启动 / sign 前调用：若命中马甲站则写入身份 + 注入 token。
 * URL/hostname 未命中时仍可从 session 恢复 token（redirect 后 ?site= 已剥掉）；
 * 未命中且无 session 时不碰任何 sessionStorage，保证既有 appid 行为不变。
 */
export function initVestSiteIdentity(
  hashParams?: Record<string, string>
): SiteConfig | null {
  const site = resolveVestSite(hashParams)
  if (site) {
    applyVestSiteToSession(site)
    applyVestSiteTokens(site)
    return site
  }
  // redirect 后 site 参数已剥掉、hostname 非 vest 域名：从 session 续 token
  const fromSession = getCurrentVestSite()
  if (fromSession) {
    applyVestSiteTokens(fromSession)
  } else {
    applyVestSiteTokens(null)
  }
  return null
}

/** 读取当前会话的站点配置（无则 null） */
export function getCurrentVestSite(): SiteConfig | null {
  try {
    const domain = sessionStorage.getItem(VEST_STORAGE_KEYS.siteDomain)
    if (domain && SITE_CONFIG_BY_DOMAIN[domain]) {
      return SITE_CONFIG_BY_DOMAIN[domain]
    }
    const siteKey = sessionStorage.getItem(VEST_STORAGE_KEYS.siteKey)
    if (siteKey && SITE_CONFIG_BY_KEY[siteKey]) {
      return SITE_CONFIG_BY_KEY[siteKey]
    }
  } catch {
    // ignore
  }
  return null
}

/**
 * 埋点请求体中的域名简称字段名（渠道主口径是入口透传的 channel，
 * site_slug 为附加站点字段）。
 */
export const VEST_SITE_SLUG_API_FIELD = 'site_slug' as const

/**
 * 工单请求体中的站点域名字段名。值为完整站点域名（不含 feedback. /
 * feedback- 前缀），管理端【域名】筛选的数据源头；待服务端确认后只改此常量。
 */
export const VEST_SITE_DOMAIN_API_FIELD = 'site_domain' as const

/**
 * 马甲站埋点附加字段：{ site_slug: 'drama' | ... }。
 * 非马甲站 appid 返回 {}，展开合并后不污染既有 app 请求体。
 * 同时要求 session 中有可解析站点，避免仅残留 slug 键时误带。
 */
export function getVestSiteSlugPayload(): Record<string, string> {
  try {
    const appid = sessionStorage.getItem('appid')
    if (!isVestAppid(appid)) return {}
    const site = getCurrentVestSite()
    if (!site?.slug) return {}
    return { [VEST_SITE_SLUG_API_FIELD]: site.slug }
  } catch {
    return {}
  }
}

/**
 * 马甲站工单附加字段：{ site_domain: 'drama.b25reel.com' | ... }。
 * 门禁与空值语义同 getVestSiteSlugPayload。
 */
export function getVestSiteDomainPayload(): Record<string, string> {
  try {
    const appid = sessionStorage.getItem('appid')
    if (!isVestAppid(appid)) return {}
    const site = getCurrentVestSite()
    if (!site?.domain) return {}
    return { [VEST_SITE_DOMAIN_API_FIELD]: site.domain }
  } catch {
    return {}
  }
}

/**
 * 马甲站接口域名前缀，按构建环境取；null = 该环境不代理，沿用默认 apiUrl。
 * 这是接口代理的唯一开关：改此表并重新构建即可切换。
 * 生产口径来自运维域名表：feedback-drama.b25reel.com 页面对应
 * feedback-api-drama.b25reel.com 接口；各接口域名只是同一后端的代理，
 * 目的是不暴露 stardustgod 关联。灰度入口 gray-feedback-<slug>.<base>
 * 打的也是 prod 构建，由 prod 键覆盖。
 * 走查结论：测试环境暂不配接口代理，test 置 null；若测试服后续也开
 * test-feedback-api-<slug>.<base> 代理，把 test 改成 'test-feedback-api-'。
 */
const VEST_API_HOST_PREFIX: Record<string, string | null> = {
  dev: null,
  test: null,
  prod: 'feedback-api-'
}

/**
 * 马甲站独立域名下的接口地址：https://<环境前缀><slug>.<base>。
 * 同时满足才返回值，否则返回 null 沿用默认 apiUrl：
 * - 当前构建环境在 VEST_API_HOST_PREFIX 中配了前缀
 * - 页面 hostname 是独立域名（[test-|gray-]feedback-<slug>.<base> 或
 *   legacy feedback.<域名>，即剥前缀后确实变短且命中站点表）；共享域名
 *   dev-h5 / localhost 靠 ?site= 解析身份，不给调试路径增加接口代理的
 *   CORS 条件。
 */
export function getVestApiUrl(hostname?: string): string | null {
  const prefix = VEST_API_HOST_PREFIX[getEnv()]
  if (!prefix) return null
  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location?.hostname : '')
  if (!host) return null
  const siteDomain = stripFeedbackPrefix(host)
  if (siteDomain === host.toLowerCase()) return null
  const site = SITE_CONFIG_BY_DOMAIN[siteDomain]
  if (!site) return null
  return 'https://' + prefix + site.domain
}

/**
 * 读取当前马甲站 logo（供 getPageConfig / Layout 使用）。
 * 未命中站点时返回空串，不抛错。
 */
export function getCurrentVestLogo(): string {
  return getCurrentVestSite()?.logo || ''
}

/**
 * 当前马甲站 Rate Service 图标（按 siteKey 色变体；缺则默认 rate.svg）。
 */
export function getCurrentVestRateIcon(): string {
  const siteKey = getCurrentVestSite()?.siteKey
  if (siteKey && VEST_RATE_ICON_BY_SITE_KEY[siteKey]) {
    return VEST_RATE_ICON_BY_SITE_KEY[siteKey]
  }
  return VEST_RATE_ICON_DEFAULT
}

/** body 应挂载的 vest 相关 class（不含 appid 本身） */
export function getVestBodyClasses(appid?: string | null): string[] {
  if (!isVestAppid(appid)) return []
  const classes = ['vest']
  try {
    const siteKey = sessionStorage.getItem(VEST_STORAGE_KEYS.siteKey)
    if (siteKey) classes.push(siteKey)
  } catch {
    // ignore
  }
  return classes
}
